/**
 * The slab a graded unit sits in: its proportions, its grader's plate, and how much of it a given
 * size can carry (TRO-627).
 *
 * This module is the data half of `SlabFrame`: pure geometry and a grader registry with no views,
 * so both are testable without a renderer, and a surface that reasons about the slab's box (the
 * foil shimmer, an overlay) can measure it without drawing one.
 *
 * The geometry is a real slab's. Every fraction below is measured off a PSA holder: 3.28" × 5.28"
 * outer, a 2.5" × 3.5" card, and a label a little over an inch tall. Graders differ by a millimetre
 * or two, which does not show at 100px, so one set of proportions serves all of them. The
 * silhouette is the whole recognition cue at grid size.
 *
 * The silhouette, the white label plate and a hairline case edge are what read as a slab. Since
 * TRO-1234 the holder is also drawn with the depth a real one has — gloss, a bevelled lip, a cast
 * shadow — which is ADR-0144's single exception to DESIGN.md §4: a depicted object may have the
 * depth the object has, a surface may not. `SlabFrame` draws it; the colours it needs live here.
 */

import { SLAB_LOGOS, type SlabLogo } from "./slab-logos";

export type { SlabLogo };

/** Outer aspect of the holder, width ÷ height (3.28" ÷ 5.28"). */
export const SLAB_ASPECT = 3.28 / 5.28;

/**
 * The holder's parts as fractions: of slab height vertically, of slab width horizontally.
 *
 * Kept as one table because they must sum: the plate's top margin, its height, the gap, the card
 * window and the bottom margin fill the holder exactly, and changing one without the others pushes
 * the card off-centre. Only `slabParts` reads them.
 */
const SLAB = {
  /** Above the label plate. */
  plateTop: 0.03,
  /** Label plate: a little over an inch of the 5.28" holder. */
  plateHeight: 1.15 / 5.28,
  /** Plate inset from each side of the holder. */
  plateInset: 0.05,
  /** Between the plate and the card window. */
  gap: 0.035,
  /** The card itself: 2.5" of the 3.28" width, 3.5" of the 5.28" height. */
  cardWidth: 2.5 / 3.28,
  cardHeight: 3.5 / 5.28,
} as const;

/**
 * How much slab a box of this size can carry.
 *
 * - `full`: the whole holder, with case, label plate and the card in the window.
 * - `compact`: no holder, only the grader's plate as a strip across the top of the art. A holder
 *   this small spends a third of its width on empty case and leaves an illegible card thumbnail;
 *   the plate is the part that still carries meaning.
 * - `seal`: the text grade chip, for sizes where a plate's logo would be a smudge.
 *
 * The ladder is keyed on measured width, not on the calling screen. A grid cell and the card-detail
 * hero are the same component at different widths, and the phone grid's cell width ranges from
 * about 97px at four columns to about 145px at two on supported devices, so a per-screen rule would
 * render the same card differently on two phones.
 */
export type SlabDensity = "full" | "compact" | "seal";

/**
 * Where `full` starts. This is a judgment call: at 92px the card window is about 58px wide, roughly
 * where a Pokémon card's art still resolves as that card rather than a coloured rectangle. Re-check
 * it on a device before moving it.
 */
export const SLAB_FULL_MIN_WIDTH = 92;
/** Where the plate strip gives way to the text chip. */
export const SLAB_COMPACT_MIN_WIDTH = 56;

export function slabDensity(artWidth: number): SlabDensity {
  if (artWidth >= SLAB_FULL_MIN_WIDTH) return "full";
  if (artWidth >= SLAB_COMPACT_MIN_WIDTH) return "compact";
  return "seal";
}

export type SlabBox = { top: number; left: number; width: number; height: number; };

/**
 * Returns the holder and its parts in px, laid out inside an art box of `artWidth` × `artHeight`.
 *
 * The holder is height-fitted: it takes the box's full height and the width its aspect asks for,
 * letterboxed left and right. That keeps graded and raw tiles on the same grid rhythm, because the
 * art box stays 3:4 either way and a wall of mixed inventory has one row height. Fitting to width
 * would make every graded tile about 20% taller than a raw one beside it.
 */
export function slabParts(
  artWidth: number,
  artHeight: number,
): { slab: SlabBox; plate: SlabBox; card: SlabBox; } {
  const height = artHeight;
  const width = Math.min(artWidth, height * SLAB_ASPECT);
  const left = (artWidth - width) / 2;
  const slab = { top: 0, left, width, height };
  const plate = {
    top: height * SLAB.plateTop,
    left: left + width * SLAB.plateInset,
    width: width * (1 - 2 * SLAB.plateInset),
    height: height * SLAB.plateHeight,
  };
  const card = {
    top: plate.top + plate.height + height * SLAB.gap,
    left: left + (width * (1 - SLAB.cardWidth)) / 2,
    width: width * SLAB.cardWidth,
    height: height * SLAB.cardHeight,
  };
  return { slab, plate, card };
}

/**
 * Returns the plate strip at `compact`, in px. It is proportional to the art with a floor and a
 * ceiling, because pure proportion makes the strip too thin to seat a logo at the narrow end of the
 * `compact` range and too heavy at the wide end.
 */
export function slabStrip(artWidth: number, artHeight: number): SlabBox {
  const height = Math.max(16, Math.min(24, artHeight * 0.15));
  return { top: 0, left: 0, width: artWidth, height };
}

/**
 * Returns where the card art goes for a density: the window inside the holder at `full`, the whole
 * box at `compact` or `seal`.
 *
 * Exported because `CardTile`'s foil shimmer also lands there: it sweeps the card, not the case,
 * and measures its travel off the width it sweeps. `cardTileArtBox` is exported for the same
 * reason.
 */
export function slabCardWindow(
  artWidth: number,
  artHeight: number,
  density: SlabDensity,
): SlabBox {
  if (density !== "full") return { top: 0, left: 0, width: artWidth, height: artHeight };
  return slabParts(artWidth, artHeight).card;
}

/* ── The grader's plate ─────────────────────────────────────────────────────────────────────── */

/**
 * How a grader signs its label plate.
 *
 * `logo` is the grader's own mark where we have a faithful copy of it (vector or raster), and
 * `wordmark` is the fallback for every other grader, set in the app's own type as a deliberate
 * second-best. `accent` is a hairline of the grader's color under the plate, present only where the
 * color came from that grader's own artwork; an invented brand color is worse than none, so an
 * unsourced grader gets no hairline.
 */
export type SlabStyle = {
  /** The normalized grader key, or `null` when the company is unknown or unrecorded. */
  key: string | null;
  logo: SlabLogo | null;
  wordmark: string;
  accent: string | null;
};

/**
 * Grader colors, each sampled from that grader's own mark rather than chosen: PSA's from the
 * artwork on their holders, CGC's and SGC's from the SVGs their sites serve. They are literal hexes
 * rather than palette tokens because they are not ours to re-point when the app is reskinned.
 *
 * Beckett and TAG are sourced the same way and arrive at the same answer. `LOGOS.md` records
 * Beckett's crest as white on opaque black and TAG's lockup as a black plate that IS the mark, so
 * black is their published colour rather than an absence of one. They therefore share an ink, and
 * a slab of each is told apart by its plate, not its case. Giving one of them something livelier —
 * Beckett's holder gold, say — is a sourcing job, not a colour to pick here (TRO-1234).
 */
const ACCENT: Readonly<Record<string, string>> = {
  PSA: "#0a4fa2",
  CGC: "#ce0e2d",
  SGC: "#101418",
  BGS: "#14171c",
  TAG: "#14171c",
};

/** A grader's colour lit and shaded, plus the hue its holder casts. */
export type SlabPlinth = { top: string; bottom: string; cast: string; };

/** Clamps to a byte, so a lightened white and a darkened black both stay in gamut. */
const byte = (value: number): number => Math.max(0, Math.min(255, Math.round(value)));

/**
 * Moves a `#rrggbb` toward white (positive) or black (negative) by a fraction of the way there.
 *
 * Kept here rather than in the theme because it only ever runs on a grader's own colour, which is
 * not ours to re-point; `palette.ts` deals in colours that are. Exported because clamping is the
 * property worth testing and the registry holds no colour extreme enough to exercise it — a
 * grader whose artwork is pure black would wrap without `byte`.
 */
export function shade(hex: string, amount: number): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  const target = amount > 0 ? 255 : 0;
  const moved = channels.map((v) => byte(v + (target - v) * Math.abs(amount)));
  return `#${moved.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

/**
 * The plinth a `SlabQuote` stands its holder on, in the grader's own colour (TRO-1234).
 *
 * Takes the company rather than its accent so a caller does not walk to a field to hand it
 * straight back. `top` and `bottom` are that one colour lit and shaded, not two colours: the
 * plinth is a single
 * object under a single light, and deriving both from the sourced hex keeps it that way even when
 * a grader's artwork is re-sampled. A grader with no sourced colour gets no plinth at all, on the
 * same rule that governs the plate's hairline.
 */
export function slabPlinth(company: string | null | undefined): SlabPlinth | null {
  const accent = slabStyle(company).accent;
  if (!accent) return null;
  return { top: shade(accent, 0.18), bottom: shade(accent, -0.3), cast: accent };
}

/**
 * The case and the plate.
 *
 * The plate is white in both themes on purpose. It is the grader's own label ground, not one of our
 * surfaces: PSA's mark is blue and red and CGC's is red and black, and a multi-color mark cannot be
 * re-inked for a dark theme the way `GemMark` can. A dark plate would either recolor someone else's
 * trademark or put their artwork on a ground it was never drawn for. A white label in a dark case
 * is also what a real slab looks like in a dim room.
 *
 * The case follows the theme, because it is our surface holding their label.
 */
export type SlabColors = {
  /** The acrylic case. */
  case: string;
  /** Its hairline edge: the No-Shadow Rule's structural line, not a lift. */
  edge: string;
  /** The grader's label ground. */
  plate: string;
  /** Ink on the plate: the grade, and any wordmark standing in for a logo. */
  ink: string;
  /** The plate's shaded foot, where the case's lip falls across it. */
  bevel: string;
  /**
   * What the holder casts. Carries its own alpha and pairs with `shadowOpacity: 1`, so one value
   * sets both the hue and the weight of the shadow per theme.
   */
  cast: string;
};

export const SLAB_COLORS: Readonly<Record<"light" | "dark", SlabColors>> = {
  // Three tonal steps, in the order a slab reads: the art box's ground (`surface-muted`) below the
  // case, the case below its label. Two steps would leave the plate looking like the whole holder.
  light: {
    case: "#f1f3f8",
    edge: "rgba(23,36,64,0.3)",
    plate: "#ffffff",
    ink: "#101418",
    bevel: "#dfe4ec",
    cast: "rgba(23,36,64,0.2)",
  },
  dark: {
    case: "#3d4c72",
    edge: "rgba(255,255,255,0.28)",
    plate: "#ffffff",
    ink: "#101418",
    // The plate stays white in both themes, so its bevel does too: it is the grader's own label
    // ground catching the case's lip, not one of our surfaces following the theme.
    bevel: "#dfe4ec",
    // Heavier and neutral: a dark room swallows a tinted shadow, and the case needs to lift off a
    // ground that is nearly as dark as the shadow itself.
    cast: "rgba(0,0,0,0.5)",
  },
};

/**
 * Returns the plate style for a grading company, however the record spells it.
 *
 * Company strings reach the UI from three places that normalize differently (the scan path, a CSV
 * import and hand entry in the Manage sheet), so the key is trimmed and upper-cased here. An
 * unrecognized company still gets a plate with its own name, because "DGS 9" is truer than the
 * anonymous "GRADED".
 *
 * A `null` company with a grade is a slab whose grader was never recorded (`formatGradeSeal`'s
 * "GRADED" case), and it gets the neutral plate.
 */
export function slabStyle(company: string | null | undefined): SlabStyle {
  const key = company?.trim().toUpperCase() || null;
  if (!key) return { key: null, logo: null, wordmark: "GRADED", accent: null };
  return {
    key,
    logo: SLAB_LOGOS[key] ?? null,
    wordmark: key,
    accent: ACCENT[key] ?? null,
  };
}
